import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import IntakePreview from './IntakePreview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FRAMEWORK_ITEMS = [
  {
    section: 'Keyword Strategy',
    color: '#185FA5', bg: '#E6F1FB', border: '#85B7EB',
    items: [
      'Services and/or product keywords discussed',
      'Sector keywords (if applicable)',
      'Location keywords (if applicable)',
      'Blog posts / objections / pain points / FAQs / hub and spoke',
      'Competitive analysis reviewed',
    ]
  },
  {
    section: 'SEO Technical Fundamentals',
    color: '#27500A', bg: '#EAF3DE', border: '#97C459',
    items: [
      'SEO audit and fixes explained',
      'Metadata updates discussed',
      'Content development plan outlined',
    ]
  },
  {
    section: 'Authority Levels / Google Trust / Domain Authority',
    color: '#633806', bg: '#FAEEDA', border: '#FAC775',
    items: [
      'Backlink acquisition strategy explained',
      'Citation development discussed',
      'Press releases and other link types covered (Barnacle links, Quora, Pinterest etc.)',
    ]
  },
  {
    section: 'Lead Generation Principles',
    color: '#26215C', bg: '#EEEDFE', border: '#AFA9EC',
    items: [
      'Conversion rate optimisation discussed',
      'Landing page improvements reviewed',
      'Messaging / offer improvements explained (value equation)',
    ]
  },
  {
    section: 'Ongoing Process',
    color: '#1A2744', bg: '#F1F5F9', border: '#CBD5E1',
    items: [
      'Continuous improvements through testing and measuring explained',
      'Detailed monthly reporting process confirmed',
    ]
  },
];

const ACCESS_PLATFORMS = [
  { key: 'cms', label: 'Content Management System / Website access' },
  { key: 'ga', label: 'Google Analytics' },
  { key: 'gsc', label: 'Google Search Console' },
  { key: 'gtm', label: 'Google Tag Manager' },
  { key: 'gbp', label: 'Google Business Profile' },
];

const TEAM_EMAIL = 'josh@improvemysearchranking.com / joshanthonyhamit@gmail.com';

function SectionHeader({ number, title, subtitle }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1A2744', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{number}</div>
        <div className="page-title" style={{ marginBottom: 0 }}>{title}</div>
      </div>
      {subtitle && <div className="page-sub" style={{ paddingLeft: 38 }}>{subtitle}</div>}
    </div>
  );
}

function StepIndicator({ step, total }) {
  const labels = ['Client', 'Framework', 'Access', 'NAP Details', 'Content Survey', 'Review'];
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', alignItems: 'center', overflowX: 'auto', paddingBottom: 4 }}>
      {labels.map((label, i) => {
        const n = i + 1; const isDone = step > n; const isActive = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: isDone ? '#3B6D11' : isActive ? '#1A2744' : 'var(--gray-200)', color: isDone || isActive ? '#fff' : 'var(--gray-500)', flexShrink: 0 }}>
                {isDone ? '✓' : n}
              </div>
              <span style={{ fontSize: 12, color: isActive ? 'var(--gray-900)' : 'var(--gray-400)', fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < labels.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--gray-200)', margin: '0 8px', minWidth: 12 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function IntakeForm({ onBack }) {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(false);
  const [strategySummary, setStrategySummary] = useState('');
  const [view, setView] = useState('form'); // 'form' | 'past'
  const [pastForms, setPastForms] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);

  // Step 1 — client
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [completedBy, setCompletedBy] = useState('');
  const [docsReviewed, setDocsReviewed] = useState({ keywordResearch: false, seoAudit: false });
  const [docsNotes, setDocsNotes] = useState('');

  // Step 2 — framework checklist
  const [frameworkChecked, setFrameworkChecked] = useState({});

  // Step 3 — access details
  const [access, setAccess] = useState({ cms: 'pending', ga: 'pending', gsc: 'pending', gtm: 'pending', gbp: 'pending' });
  const [accessNotes, setAccessNotes] = useState({});

  // Step 4 — NAP
  const [nap, setNap] = useState({ businessName: '', ownerName: '', address: '', phone: '', citationContact: '', foundedYear: '', openingHours: '', description: '' });

  // Step 5 — content survey
  const [survey, setSurvey] = useState({
    mainFocus: '', usps: '', seasonal: '',
    ageRange: '', gender: '', occupation: '', location: '', otherDemographics: '',
    customerChallenges: '',
    competitors: '', industryTrends: '',
    brandVoice: 'professional', brandVoiceOther: '',
    avoidLanguage: '', keywordsStyle: '',
    jobsCompleted: '', notableClients: '',
    qualifications: '', subjectExperts: '', originalResearch: '',
    publications: '', partnerships: '', awards: '',
    testimonials: '', guarantees: '', compliance: '',
    additionalNotes: '',
  });

  useEffect(() => {
    supabase.from('clients').select('id, name, url, description, phone, email, years_trading, team_size, accreditations, awards, clients_served, notable_clients, stats, unique_selling_points').order('name')
      .then(({ data }) => { if (data) setClients(data); });
  }, []);

  useEffect(() => {
    if (clientId) {
      const c = clients.find(c => c.id === clientId);
      if (c) {
        setClientName(c.name);
        // Pre-fill NAP from client profile
        setNap(prev => ({
          ...prev,
          businessName: c.name || prev.businessName,
          phone: c.phone || prev.phone,
          description: c.description || prev.description,
        }));
        // Pre-fill survey from client profile
        setSurvey(prev => ({
          ...prev,
          usps: c.unique_selling_points || prev.usps,
          jobsCompleted: c.clients_served || prev.jobsCompleted,
          notableClients: c.notable_clients || prev.notableClients,
          qualifications: c.accreditations || prev.qualifications,
          awards: c.awards || prev.awards,
        }));
      }
    }
  }, [clientId, clients]);

  const toggleFramework = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setFrameworkChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allFrameworkChecked = () => {
    let total = 0;
    FRAMEWORK_ITEMS.forEach((s, si) => s.items.forEach((_, ii) => { total++; }));
    return Object.values(frameworkChecked).filter(Boolean).length === total;
  };

  const upSurvey = (f, v) => setSurvey(p => ({ ...p, [f]: v }));
  const upNap = (f, v) => setNap(p => ({ ...p, [f]: v }));

  const generateSummary = async (formData) => {
    setGenerating(true);
    try {
      const prompt = `You are a senior SEO strategist. Based on the following client intake information, write a concise SEO campaign strategy summary of 200-280 words. This will be included in the kick-off document.

CLIENT: ${formData.clientName}
BUSINESS FOCUS: ${formData.survey.mainFocus}
USPs: ${formData.survey.usps}
TARGET CUSTOMERS: Age ${formData.survey.ageRange}, ${formData.survey.gender}, ${formData.survey.occupation}, Location: ${formData.survey.location}
CUSTOMER CHALLENGES: ${formData.survey.customerChallenges}
COMPETITORS: ${formData.survey.competitors}
INDUSTRY TRENDS: ${formData.survey.industryTrends}
BRAND VOICE: ${formData.survey.brandVoice}
QUALIFICATIONS: ${formData.survey.qualifications}
AWARDS: ${formData.survey.awards}
TESTIMONIALS/TRUST: ${formData.survey.testimonials}
ADDITIONAL NOTES: ${formData.survey.additionalNotes}

Write a clear, professional strategy summary covering:
1. Overview of the business and opportunity
2. Key SEO priorities based on the information provided
3. Target audience and keyword approach
4. Trust and authority signals to leverage
5. Key success factors for this campaign

Use UK English. Be specific to this client — avoid generic statements. Write in flowing paragraphs.`;

      const res = await fetch(`${API_URL}/api/content/meta-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, maxTokens: 600 })
      });
      const data = await res.json();
      setStrategySummary(data.result || '');
    } catch (e) {
      console.error('Summary generation failed:', e);
    }
    setGenerating(false);
  };

  const handleFinish = async () => {
    const formData = { clientId, clientName, completedBy, docsReviewed, docsNotes, frameworkChecked, access, accessNotes, nap, survey };
    await generateSummary(formData);
    setPreview(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const payload = {
        client_id: clientId || null,
        client_name: clientName,
        completed_by: completedBy,
        framework_reviewed: frameworkChecked,
        access_details: { access, accessNotes },
        nap,
        content_survey: survey,
        strategy_summary: strategySummary,
      };
      if (savedId) {
        const { error } = await supabase.from('intake_forms').update(payload).eq('id', savedId);
        if (error) { console.error('Update error:', error); return; }
      } else {
        const { data, error } = await supabase.from('intake_forms').insert(payload).select('id').single();
        if (error) { console.error('Insert error:', error); return; }
        if (data?.id) setSavedId(data.id);
        console.log('Saved intake form with id:', data?.id);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) { console.error('Save error:', e); }
    setSaving(false);
  };

  const loadPastForms = async () => {
    setLoadingPast(true);
    const { data, error } = await supabase
      .from('intake_forms')
      .select('id, client_name, completed_by, created_at, nap, strategy_summary')
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) console.error('loadPastForms error:', error);
    console.log('Past forms loaded:', data?.length, data);
    setPastForms(data || []);
    setLoadingPast(false);
  };

  const loadForm = async (id) => {
    const { data } = await supabase
      .from('intake_forms')
      .select('*')
      .eq('id', id)
      .single();
    if (!data) return;
    setSavedId(data.id);
    setClientId(data.client_id || '');
    setClientName(data.client_name || '');
    setCompletedBy(data.completed_by || '');
    setFrameworkChecked(data.framework_reviewed || {});
    setAccess(data.access_details?.access || { cms: 'pending', ga: 'pending', gsc: 'pending', gtm: 'pending', gbp: 'pending' });
    setAccessNotes(data.access_details?.accessNotes || {});
    setNap(data.nap || { businessName: '', ownerName: '', address: '', phone: '', citationContact: '', foundedYear: '', openingHours: '', description: '' });
    setSurvey(data.content_survey || {});
    setStrategySummary(data.strategy_summary || '');
    setDocsReviewed({ keywordResearch: false, seoAudit: false });
    setView('form');
    setStep(1);
  };

  // ── Preview ─────────────────────────────────────────────────
  if (preview) {
    return (
      <IntakePreview
        data={{ clientId, clientName, completedBy, docsReviewed, docsNotes, frameworkChecked, access, accessNotes, nap, survey }}
        strategySummary={strategySummary}
        generating={generating}
        onBack={() => setPreview(false)}
        onSave={handleSave}
        saving={saving}
        saveSuccess={saveSuccess}
      />
    );
  }

  // ── Step 1: Client ───────────────────────────────────────────
  // ── Past forms view ──────────────────────────────────────────
  if (view === 'past') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div style={{ flex: 1 }}>
          <div className="page-title">Past Kick-Off Forms</div>
          <div className="page-sub">Load a previously saved intake form</div>
        </div>
        <button className="btn btn-sm" onClick={() => setView('form')}>+ New form</button>
      </div>

      {loadingPast ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
          <span className="spinner" /> Loading…
        </div>
      ) : pastForms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
          No saved forms yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {pastForms.map(f => (
            <div key={f.id} className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{f.client_name || 'Unnamed client'}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  {f.nap?.businessName && f.nap.businessName !== f.client_name && <span>{f.nap.businessName} · </span>}
                  {f.completed_by && <span>Completed by {f.completed_by} · </span>}
                  {new Date(f.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                {f.strategy_summary && (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>
                    {f.strategy_summary.substring(0, 120)}…
                  </div>
                )}
              </div>
              <button className="btn btn-sm" onClick={() => loadForm(f.id)}>Load →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (step === 1) return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
        {saveSuccess && <span style={{ fontSize: 12, color: 'var(--green)', alignSelf: 'center' }}>✓ Saved</span>}
        <button className="btn btn-sm" onClick={() => { loadPastForms(); setView('past'); }}>📋 Past forms</button>
      </div>
      <StepIndicator step={1} />
      <SectionHeader number="1" title="Client & documents" subtitle="Select the client and confirm which documents have been reviewed before the call." />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Client details</div>
        <div className="form-group">
          <label>Select existing client</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">— Select a client or enter name below —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {!clientId && (
          <div className="form-group">
            <label>Client name (if not in system)</label>
            <input type="text" placeholder="e.g. Carpet Bright UK" value={clientName} onChange={e => setClientName(e.target.value)} />
          </div>
        )}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Team member completing this form</label>
          <input type="text" placeholder="e.g. Josh" value={completedBy} onChange={e => setCompletedBy(e.target.value)} />
        </div>
      </div>

      <div className="card">
        <div className="card-title">Documents reviewed before this call</div>
        <div className="card-sub">Confirm which documents have been reviewed and are ready to reference during the kick-off.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {[['keywordResearch', 'Initial keyword research'], ['seoAudit', 'SEO audit']].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 14px', borderRadius: 'var(--radius-md)', border: `1px solid ${docsReviewed[key] ? 'var(--blue-border)' : 'var(--gray-200)'}`, background: docsReviewed[key] ? 'var(--blue-light)' : 'var(--gray-50)', flex: 1 }}>
              <input type="checkbox" checked={docsReviewed[key]} onChange={e => setDocsReviewed(p => ({ ...p, [key]: e.target.checked }))} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
            </label>
          ))}
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Notes from documents (optional)</label>
          <textarea rows={2} placeholder="Key findings or points to raise during the call" value={docsNotes} onChange={e => setDocsNotes(e.target.value)} />
        </div>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={onBack}>← Tools</button>
        <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!clientName.trim()}>
          Next: Our framework →
        </button>
      </div>
    </div>
  );

  // ── Step 2: Framework ────────────────────────────────────────
  if (step === 2) return (
    <div>
      <StepIndicator step={2} />
      <SectionHeader number="2" title="Our Framework for SEO Success" subtitle="Walk the client through each point, ticking as you go. This ensures nothing is missed." />

      {FRAMEWORK_ITEMS.map((section, si) => (
        <div key={si} className="card" style={{ marginBottom: 12, borderLeft: `4px solid ${section.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: section.color, marginBottom: 12 }}>{section.section}</div>
          {section.items.map((item, ii) => {
            const key = `${si}-${ii}`;
            const checked = !!frameworkChecked[key];
            return (
              <label key={ii} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 8, padding: '8px 10px', borderRadius: 'var(--radius-md)', background: checked ? section.bg : 'transparent', transition: 'background 0.1s' }}>
                <input type="checkbox" checked={checked} onChange={() => toggleFramework(si, ii)} style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: checked ? section.color : 'var(--gray-700)', fontWeight: checked ? 500 : 400, lineHeight: 1.4, textDecoration: checked ? 'none' : 'none' }}>{item}</span>
              </label>
            );
          })}
        </div>
      ))}

      {!allFrameworkChecked() && (
        <div className="alert alert-info">Tick each point as you cover it with the client.</div>
      )}

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(1)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(3)}>Next: Access details →</button>
      </div>
    </div>
  );

  // ── Step 3: Access ───────────────────────────────────────────
  if (step === 3) return (
    <div>
      <StepIndicator step={3} />
      <SectionHeader number="3" title="Access details" subtitle={`Ask the client to add the following email as a user on each platform: ${TEAM_EMAIL}`} />

      <div className="alert alert-info" style={{ marginBottom: 16 }}>
        Ask client to add: <strong>{TEAM_EMAIL}</strong> — we can create any of these platforms for the client if they do not have them.
      </div>

      {ACCESS_PLATFORMS.map(({ key, label }) => (
        <div className="card" key={key} style={{ marginBottom: 10, padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{label}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['granted', 'Access granted', '#EAF3DE', '#27500A', '#97C459'], ['pending', 'Pending', '#FAEEDA', '#633806', '#FAC775'], ['create', 'Need to create', '#EEEDFE', '#26215C', '#AFA9EC'], ['na', 'N/A', '#F1EFE8', '#444441', '#B4B2A9']].map(([val, lbl, bg, color, border]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', padding: '5px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: access[key] === val ? bg : 'var(--gray-50)', color: access[key] === val ? color : 'var(--gray-500)', border: `1px solid ${access[key] === val ? border : 'var(--gray-200)'}`, transition: 'all 0.1s' }}>
                  <input type="radio" name={key} value={val} checked={access[key] === val} onChange={() => setAccess(p => ({ ...p, [key]: val }))} style={{ display: 'none' }} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <input type="text" placeholder="Notes (e.g. login URL, username, action required)" value={accessNotes[key] || ''} onChange={e => setAccessNotes(p => ({ ...p, [key]: e.target.value }))} style={{ fontSize: 12 }} />
          </div>
        </div>
      ))}

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(2)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(4)}>Next: NAP details →</button>
      </div>
    </div>
  );

  // ── Step 4: NAP ──────────────────────────────────────────────
  if (step === 4) return (
    <div>
      <StepIndicator step={4} />
      <SectionHeader number="4" title="NAP details" subtitle="Confirm Name, Address and Phone details for citations and SEO. Consistency across the web is critical." />

      <div className="card">
        <div className="form-row form-row-2 form-group">
          <div><label>Business name *</label><input type="text" placeholder="Exact name as it should appear everywhere" value={nap.businessName} onChange={e => upNap('businessName', e.target.value)} /></div>
          <div><label>Owner's name</label><input type="text" placeholder="e.g. John Smith" value={nap.ownerName} onChange={e => upNap('ownerName', e.target.value)} /></div>
        </div>
        <div className="form-group">
          <label>Business address *</label>
          <textarea rows={2} placeholder="Full address including postcode" value={nap.address} onChange={e => upNap('address', e.target.value)} />
        </div>
        <div className="form-row form-row-2 form-group">
          <div><label>Business phone number *</label><input type="text" placeholder="e.g. 01737 773 768" value={nap.phone} onChange={e => upNap('phone', e.target.value)} /></div>
          <div><label>Contact name for citations</label><input type="text" placeholder="Name to use on business directories" value={nap.citationContact} onChange={e => upNap('citationContact', e.target.value)} /></div>
        </div>
        <div className="form-row form-row-2 form-group">
          <div><label>Founded year</label><input type="text" placeholder="e.g. 2008" value={nap.foundedYear} onChange={e => upNap('foundedYear', e.target.value)} /></div>
          <div><label>Opening hours</label><input type="text" placeholder="e.g. Mon–Fri 8am–6pm, Sat 9am–3pm" value={nap.openingHours} onChange={e => upNap('openingHours', e.target.value)} /></div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Business description (take from website)</label>
          <textarea rows={3} placeholder="Copy from their About page or homepage — keep consistent" value={nap.description} onChange={e => upNap('description', e.target.value)} />
        </div>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(3)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(5)}>Next: Content survey →</button>
      </div>
    </div>
  );

  // ── Step 5: Content survey ───────────────────────────────────
  if (step === 5) return (
    <div>
      <StepIndicator step={5} />
      <SectionHeader number="5" title="SEO Content Survey" subtitle="Work through each section with the client. Pre-filled fields can be updated." />

      {/* Section 1 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#185FA5', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 1: About Your Business</div>
        <div className="form-group"><label>What is the main focus of your business?</label><textarea rows={2} placeholder="Products/services, industry, or niche" value={survey.mainFocus} onChange={e => upSurvey('mainFocus', e.target.value)} /></div>
        <div className="form-group"><label>What sets your business apart from competitors? (USPs)</label><textarea rows={2} placeholder="What makes you different or better?" value={survey.usps} onChange={e => upSurvey('usps', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Seasonal trends or promotional peaks?</label><textarea rows={2} placeholder="E.g. busier in summer, Christmas promotions etc." value={survey.seasonal} onChange={e => upSurvey('seasonal', e.target.value)} /></div>
      </div>

      {/* Section 2 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#27500A', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 2: About Your Customers</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>Customer demographics</div>
        <div className="form-row form-row-2 form-group">
          <div><label>Age range</label><input type="text" placeholder="e.g. 35–55" value={survey.ageRange} onChange={e => upSurvey('ageRange', e.target.value)} /></div>
          <div><label>Gender</label><input type="text" placeholder="e.g. Predominantly male" value={survey.gender} onChange={e => upSurvey('gender', e.target.value)} /></div>
        </div>
        <div className="form-row form-row-2 form-group">
          <div><label>Occupation / industry</label><input type="text" placeholder="e.g. Homeowners, landlords, SMEs" value={survey.occupation} onChange={e => upSurvey('occupation', e.target.value)} /></div>
          <div><label>Location</label><input type="text" placeholder="e.g. South East London, Kent" value={survey.location} onChange={e => upSurvey('location', e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Other demographics</label><input type="text" placeholder="Any other relevant customer characteristics" value={survey.otherDemographics} onChange={e => upSurvey('otherDemographics', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>What problems do your customers face that you solve?</label><textarea rows={2} placeholder="Key pain points and challenges" value={survey.customerChallenges} onChange={e => upSurvey('customerChallenges', e.target.value)} /></div>
      </div>

      {/* Section 3 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#633806', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 3: Competitor & Market Insights</div>
        <div className="form-group"><label>Who are your primary competitors?</label><textarea rows={2} placeholder="List competitor names and/or websites" value={survey.competitors} onChange={e => upSurvey('competitors', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Emerging trends or industry changes?</label><textarea rows={2} placeholder="Any trends customers should know about" value={survey.industryTrends} onChange={e => upSurvey('industryTrends', e.target.value)} /></div>
      </div>

      {/* Section 4 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#26215C', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 4: Content & Tone</div>
        <div className="form-group">
          <label>Brand voice</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['professional', 'Professional & formal'], ['friendly', 'Friendly & conversational'], ['bold', 'Bold & innovative'], ['other', 'Other']].map(([val, lbl]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: survey.brandVoice === val ? '#EEEDFE' : 'var(--gray-50)', color: survey.brandVoice === val ? '#26215C' : 'var(--gray-600)', border: `1px solid ${survey.brandVoice === val ? '#AFA9EC' : 'var(--gray-200)'}` }}>
                <input type="radio" name="brandVoice" value={val} checked={survey.brandVoice === val} onChange={() => upSurvey('brandVoice', val)} style={{ display: 'none' }} />
                {lbl}
              </label>
            ))}
          </div>
          {survey.brandVoice === 'other' && <input type="text" placeholder="Describe brand voice" value={survey.brandVoiceOther} onChange={e => upSurvey('brandVoiceOther', e.target.value)} style={{ marginTop: 8 }} />}
        </div>
        <div className="form-group"><label>Is there anything we should avoid saying?</label><textarea rows={2} placeholder="Words, phrases, topics to avoid" value={survey.avoidLanguage} onChange={e => upSurvey('avoidLanguage', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Specific keywords, jargon or styles to use or avoid?</label><textarea rows={2} placeholder="Industry terms, preferred language etc." value={survey.keywordsStyle} onChange={e => upSurvey('keywordsStyle', e.target.value)} /></div>
      </div>

      {/* Section 5 — EEAT */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1A2744', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 5: Google E-E-A-T</div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8, marginTop: 4 }}>Experience</div>
        <div className="form-row form-row-2 form-group">
          <div><label>Jobs / projects completed (estimate)</label><input type="text" placeholder="e.g. Over 2,000 jobs" value={survey.jobsCompleted} onChange={e => upSurvey('jobsCompleted', e.target.value)} /></div>
          <div><label>Notable businesses or people worked with</label><input type="text" placeholder="e.g. NHS, Tesco, local councils" value={survey.notableClients} onChange={e => upSurvey('notableClients', e.target.value)} /></div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>Expertise</div>
        <div className="form-group"><label>Qualifications, certifications or training</label><textarea rows={2} placeholder="e.g. NCCA member, Which? Trusted Trader" value={survey.qualifications} onChange={e => upSurvey('qualifications', e.target.value)} /></div>
        <div className="form-row form-row-2 form-group">
          <div><label>Subject matter experts to highlight</label><input type="text" placeholder="Key team members and their expertise" value={survey.subjectExperts} onChange={e => upSurvey('subjectExperts', e.target.value)} /></div>
          <div><label>Original research, data or insights</label><input type="text" placeholder="Any proprietary data or studies" value={survey.originalResearch} onChange={e => upSurvey('originalResearch', e.target.value)} /></div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>Authority</div>
        <div className="form-group"><label>Featured in publications, media or industry websites?</label><textarea rows={2} placeholder="Press coverage, guest posts, mentions" value={survey.publications} onChange={e => upSurvey('publications', e.target.value)} /></div>
        <div className="form-row form-row-2 form-group">
          <div><label>Partnerships, affiliations or trade memberships</label><input type="text" placeholder="e.g. FSB member, local chamber of commerce" value={survey.partnerships} onChange={e => upSurvey('partnerships', e.target.value)} /></div>
          <div><label>Awards or recognitions</label><input type="text" placeholder="e.g. Best Local Business 2023" value={survey.awards} onChange={e => upSurvey('awards', e.target.value)} /></div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>Trustworthiness</div>
        <div className="form-group"><label>Customer testimonials or reviews we can use?</label><textarea rows={2} placeholder="Where are reviews hosted? Can we use specific quotes?" value={survey.testimonials} onChange={e => upSurvey('testimonials', e.target.value)} /></div>
        <div className="form-group"><label>Guarantees, warranties, insurance or trust policies</label><textarea rows={2} placeholder="e.g. 100% satisfaction guarantee, fully insured, money-back guarantee" value={survey.guarantees} onChange={e => upSurvey('guarantees', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Compliance standards, accreditations or legal requirements</label><textarea rows={2} placeholder="Industry-specific compliance, certifications" value={survey.compliance} onChange={e => upSurvey('compliance', e.target.value)} /></div>
      </div>

      {/* Section 6 */}
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-700)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Section 6: Closing</div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Anything else we should know?</label><textarea rows={3} placeholder="Any additional information about the business or customers" value={survey.additionalNotes} onChange={e => upSurvey('additionalNotes', e.target.value)} /></div>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(4)}>← Back</button>
        <button className="btn btn-primary" onClick={handleFinish} disabled={generating}>
          {generating ? <><span className="spinner" /> Generating summary…</> : 'Generate kick-off document →'}
        </button>
      </div>
    </div>
  );

  return null;
}
